import * as React from "react";
import Checkbox from "@mui/material/Checkbox";
import {
  Box,
  Paper,
  Divider,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AssignmentHeaderInfo from "../../components/assignment/AssignmentHeaderInfo";
import PostAddIcon from "@mui/icons-material/PostAdd";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme, alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchSplitBatch } from "../../services/splitBatchService.js";
import { saveNfeAssignment } from "../../services/saveNfeAssignment.js";
import { fetchDepositos } from "../../services/depositoService.js";
import { continueProcess } from "../../services/processContinueService.js";
import MessagesPanel from "../../components/assignment/MessagesPanel";
import { translateBackendMessage } from "../../utilities/translateBackendMessage";

function stepsToMessageRows(stepsExecuted) {
  const rows = [];
  (stepsExecuted || []).forEach((s) => {
    (s.messages || []).forEach((m, idx) => {
      rows.push({
        id: `${s.step}-${idx}-${Math.random()}`,
        type: m.TYPE,
        text: m.MESSAGE,
        msgIdCode: m.ID,
        msgNumber: m.NUMBER,
      });
    });
  });
  return rows;
}

const isDepositValid = (v) => {
  const s = String(v ?? "").trim();
  return s.length > 0;
};

const groupSplitByItem = (rows) => {
  const map = {};

  rows.forEach((r) => {
    if (r.batch) {
      const key = r.originalItem;

      if (!map[key]) map[key] = 0;
      map[key] += Number(r.qty || 0);
    }
  });

  return map;
};

const buildValidationMap = (rows, t, isTransfer = false) => {
  const originalMap = {};
  const splitSumMap = {};
  const errorMap = {};
  const shownErrorByItem = {};

  // 1) quantidade original por item_nf
  rows.forEach((r) => {
    const itemKey = String(r.item_nf ?? "").trim();

    if (!itemKey) return;

    if (originalMap[itemKey] === undefined) {
      originalMap[itemKey] = Number(r.originalQty || 0);
    }
  });

  rows.forEach((r) => {
    const itemKey = String(r.item_nf ?? "").trim();

    if (!itemKey) return;

    if (!splitSumMap[itemKey]) {
      splitSumMap[itemKey] = 0;
    }

    splitSumMap[itemKey] += Number(r.qty || 0);
  });

  // 3) valida linha a linha
  rows.forEach((r) => {
    const itemKey = String(r.item_nf ?? "").trim();
    const originalQty = Number(originalMap[itemKey] || 0);
    const splitQty = Number(splitSumMap[itemKey] || 0);

    if (!isTransfer && !r.batch && r.adm_lote === true) {
      errorMap[r.id] = t("enterQty.batchRequired");
      return;
    }

    if (!isTransfer && !r.warehouse) {
      errorMap[r.id] = t("enterQty.warehouseRequired");
      return;
    }

    if (!r.qty || Number(r.qty) <= 0) {
      errorMap[r.id] = t("enterQty.invalidQty");
      return;
    }

    // mostra a mensagem de soma só em uma linha do item
    if (splitQty > originalQty) {
      if (!shownErrorByItem[itemKey]) {
        errorMap[r.id] = t("enterQty.qtyExceeded", { itemKey, splitQty, originalQty });
        shownErrorByItem[itemKey] = true;
      }
      return;
    }

    if (splitQty < originalQty) {
      if (!shownErrorByItem[itemKey]) {
        errorMap[r.id] = t("enterQty.qtyBelow", { itemKey, splitQty, originalQty });
        shownErrorByItem[itemKey] = true;
      }
    }
  });

  return errorMap;
};

const getOriginalQtyMap = (rows) => {
  const map = {};

  rows.forEach((r) => {
    if (!r.isDuplicate) {
      map[r.item_nf] = Number(r.qty || 0);
    }
  });

  return map;
};

const validateSplits = (rows) => {
  const originalMap = {};
  const splitMap = {};

  rows.forEach((r) => {
    if (!r.isDuplicate) {
      originalMap[r.item_nf] = Number(r.qty || 0);
    }

    if (r.batch) {
      const key = r.isDuplicate ? r.originalItem : r.item_nf;

      if (!splitMap[key]) splitMap[key] = 0;
      splitMap[key] += Number(r.qty || 0);
    }
  });

  for (const item in splitMap) {
    if (splitMap[item] > (originalMap[item] || 0)) {
      return `enterQty.lotExceeds:${item}`;
    }
  }

  return null;
};

const EnterQtyPage = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const chipBg = (color, pastelBg) => (isDark ? alpha(color, 0.18) : pastelBg);
  const navigate = useNavigate();
  const { accessKey } = useParams();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = React.useState("");
  const [processMessages, setProcessMessages] = React.useState([]);
  const [isSaved, setIsSaved] = React.useState(false);
  const [depositos, setDepositos] = React.useState([]);

  const selected = location.state?.selected;
  // Transferência (STO) não pede depósito nesta tela — só quantidade e lote.
  const isTransfer = String(selected?.process_description || "").toLowerCase().includes("transfer");
  const [selectedId, setSelectedId] = React.useState(null);

  React.useEffect(() => {
    if (!selected) navigate("/inbound", { replace: true });
  }, [selected, navigate]);

  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    if (!accessKey) return;

    async function loadItems() {
      try {
        const items = await fetchSplitBatch(accessKey);

        const mappedItems = (items || []).map((item, index) => ({
          id:
            item.id ??
            `${accessKey}-${item.item_nf ?? item.item ?? "sem-item"}-${index}`,

          item_nf: item.item_nf ?? item.item ?? "",
          subitem: item.subitem ?? index + 1,
          description_material_xml:
            item.description_material_xml ??
            item.material_description ??
            item.short_text ??
            "",

          qty: Number(item.qty ?? item.quantity ?? 0),
          originalQty: Number(
            item.original_quantity ?? item.quantity ?? item.qty ?? 0,
          ),

          uom_xml: item.uom_xml ?? "",
          purchase_order: item.purchase_order ?? "",
          document_number: item.document_number ?? "",
          order_item: item.order_item ?? "",
          document_item: item.document_item ?? "",
          warehouse: item.warehouse  ?? "",
          batch:     item.batch      ?? "",
          plant:     item.plant      ?? "",
          adm_lote:  item.adm_lote   ?? null,
          isDuplicate: false,
          originalItem: item.item_nf ?? item.item ?? "",
        }));

        setRows(mappedItems);

        const plants = [...new Set(mappedItems.map((r) => r.plant).filter(Boolean))];
        if (plants.length > 0) {
          try {
            const rawCentro = String(plants[0]);
            const centro = /^\d+$/.test(rawCentro) ? rawCentro.padStart(4, '0') : rawCentro;
            const deps = await fetchDepositos({ centro });
            setDepositos(deps ?? []);
          } catch (err) {
            console.error("Erro ao buscar depósitos:", err);
          }
        }
      } catch (err) {
        console.error("Erro ao buscar itens NFE:", err);
      }
    }

    loadItems();
  }, [accessKey]);

  React.useEffect(() => {
    if (depositos.length === 1) {
      setRows((prev) =>
        prev.map((r) => (!r.warehouse ? { ...r, warehouse: depositos[0].deposito } : r))
      );
    }
  }, [depositos]);

  const errorMap = buildValidationMap(rows, t, isTransfer);

  const rowsWithMessage = rows.map((r) => ({
    ...r,
    message: errorMap[r.id] || "",
  }));

  const [headerInfo, setHeaderInfo] = React.useState(selected ?? {});

  const handleCheckboxChange = (id) => {
    if (String(selectedId) === String(id)) {
      setSelectedId(null);
      return;
    }
    setSelectedId(id);
  };

  const updateRowField = React.useCallback(
    (id, field, value) => {
      if (isSaved) return;

      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
      );

      setProcessMessages([]);
    },
    [isSaved],
  );

  const handleAddDocument = React.useCallback(() => {
    if (isSaved) return;
    if (!selectedId) return;

    const selectedRow = rows.find(
      (row) => String(row.id) === String(selectedId),
    );

    if (!selectedRow) return;

    const newRow = {
      ...selectedRow,
      id: `${selectedRow.id}-${Date.now()}`,
      isDuplicate: true,
      originalItem: selectedRow.originalItem || selectedRow.item_nf,
      batch: "",
      warehouse: "",
      qty: "",
    };

    const selectedIndex = rows.findIndex(
      (row) => String(row.id) === String(selectedId),
    );

    setRows((prev) => {
      const updated = [...prev];
      updated.splice(selectedIndex + 1, 0, newRow);
      return updated;
    });
  }, [selectedId, rows, isSaved]);

  const handleDeleteRow = React.useCallback(() => {
    if (isSaved) return;
    if (!selectedId) return;

    setRows((prev) =>
      prev.filter((row) => String(row.id) !== String(selectedId)),
    );
    setSelectedId(null);
  }, [selectedId, isSaved]);

  function buildPayload() {
    const splitLotes = [];
    const subItemCounter = {};

    rowsWithMessage.forEach((r) => {
      const key = r.originalItem || r.item_nf;

      if (!subItemCounter[key]) {
        subItemCounter[key] = 1;
      } else {
        subItemCounter[key] += 1;
      }

      splitLotes.push({
        accesskey: accessKey,
        item_nf: key,
        subitem: subItemCounter[key],
        quantity: Number(r.qty || 0),
        batch: r.batch,
        warehouse: r.warehouse,
        plant: r.plant,
      });
    });

    return { accessKey, splitLotes };
  }

  function setSingleMessage(type, text, msgIdCode = "", msgNumber = "") {
    setProcessMessages([{ id: `${Date.now()}-${Math.random()}`, type, text, msgIdCode, msgNumber }]);
  }

  function validateRows() {
    const hasError = rowsWithMessage.some((r) => r.message);
    if (hasError) {
      const firstError = rowsWithMessage.find((r) => r.message)?.message;
      setSingleMessage("E", firstError || t("enterQty.tableErrors"));
      return false;
    }
    return true;
  }

  async function handleSave() {
    if (!validateRows()) return;
    try {
      await saveNfeAssignment(buildPayload());
      setSingleMessage("S", t("enterQty.dataSaved"));
    } catch (err) {
      console.error("Erro ao salvar:", err);
      setSingleMessage("E", t("enterQty.saveError", { msg: translateBackendMessage(err.message, isEnglish) }));
    }
  }

  async function handleProcess() {
    if (!validateRows()) return;
    try {
      await saveNfeAssignment(buildPayload());
      setIsSaved(true);
      setSingleMessage("S", t("enterQty.saving"));

      const result = await continueProcess(accessKey, {
        selected,
        docType: location.state?.docType ?? null,
        executeManual: true,
      });

      const rows = stepsToMessageRows(result?.stepsExecuted);
      const hasError = rows.some((r) => ['E', 'A'].includes(String(r.type).toUpperCase()));

      if (hasError) {
        setIsSaved(false);
        setProcessMessages(rows);
      } else {
        setSingleMessage("S", t("enterQty.saveSuccess"));
        setTimeout(() => navigate("/inbound", { replace: true }), 1500);
      }
    } catch (err) {
      console.error("Erro ao processar:", err);
      setIsSaved(false);
      setSingleMessage("E", t("enterQty.saveError", { msg: translateBackendMessage(err.message, isEnglish) }));
    }
  }

  const totalOriginal = {};
  const totalSplit = {};

  rows.forEach((r) => {
    if (!r.isDuplicate) {
      totalOriginal[r.item_nf] = Number(r.originalQty || 0);
    }

    if (r.batch) {
      const key = r.isDuplicate ? r.originalItem : r.item_nf;

      if (!totalSplit[key]) totalSplit[key] = 0;
      totalSplit[key] += Number(r.qty || 0);
    }
  });

  let hasQtyError = false;

  for (const item in totalSplit) {
    if (totalSplit[item] > (totalOriginal[item] || 0)) {
      hasQtyError = true;
    }
  }

  const columns = React.useMemo(
    () => [
      {
        field: "select",
        headerName: "",
        width: 55,
        renderCell: (params) => (
          <Checkbox
            size="small"
            checked={String(selectedId) === String(params.id)}
            onChange={() => handleCheckboxChange(params.id)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },

      { field: "item_nf", headerName: t("assing.item"), width: 90 },
      { field: "subitem", headerName: t("enterQty.subitem"), width: 90 },

      {
        field: "description_material_xml",
        headerName: t("assing.itemDescr"),
        flex: 1,
        minWidth: 220,
      },

      {
        field: "qty",
        headerName: t("assing.qty"),
        width: 110,
        renderCell: (params) => (
          <TextField
            value={params.value ?? ""}
            size="small"
            disabled={isSaved}
            onChange={(e) => updateRowField(params.id, "qty", e.target.value)}
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontSize: 13, padding: '2px 6px', fontFamily: 'inherit' } }}
          />
        ),
      },

      { field: "uom_xml", headerName: t("assing.unit"), width: 120 },

      {
        field: "purchase_order",
        headerName: t("assing.purchaseOrderItem"),
        width: 170,
        renderCell: (params) => {
          const po = params.row.purchase_order ?? "";
          const item = params.row.order_item ?? "";

          if (!po && !item) return "";

          return `${po}${item ? " / " + item : ""}`;
        },
      },

      {
        field: "document_number",
        headerName: t("enterQty.receipt"),
        width: 150,
        renderCell: (params) => {
          const delivery = params.row.document_number ?? "";
          const item = params.row.document_item ?? "";

          if (!delivery && !item) return "";

          return `${delivery}${item ? " / " + item : ""}`;
        },
      },
      {
        field: "warehouse",
        headerName: t("enterQty.deposit"),
        width: 280,
        renderCell: (params) => {
          if (depositos.length === 0) {
            return (
              <TextField
                value={params.value ?? ""}
                size="small"
                disabled={isSaved}
                onChange={(e) =>
                  updateRowField(params.id, "warehouse", e.target.value)
                }
                fullWidth
                sx={{ '& .MuiInputBase-input': { fontSize: 13, padding: '2px 6px', fontFamily: 'inherit' } }}
              />
            );
          }
          return (
            <Select
              value={params.value ?? ""}
              size="small"
              disabled={isSaved}
              onChange={(e) =>
                updateRowField(params.id, "warehouse", e.target.value)
              }
              fullWidth
              displayEmpty
              sx={{ fontSize: 13 }}
              MenuProps={{ PaperProps: { sx: { minWidth: 320 } } }}
            >
              <MenuItem value="" disabled>
                <em>{t("enterQty.select")}</em>
              </MenuItem>
              {depositos.map((d) => (
                <MenuItem key={d.deposito} value={d.deposito} sx={{ fontSize: 13 }}>
                  {d.deposito} – {d.nome}
                </MenuItem>
              ))}
            </Select>
          );
        },
      },
      {
        field: "batch",
        headerName: t("enterQty.batch"),
        width: 150,
        renderCell: (params) => (
          <TextField
            value={params.value ?? ""}
            size="small"
            disabled={isSaved}
            onChange={(e) => updateRowField(params.id, "batch", e.target.value)}
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontSize: 13, padding: '2px 6px', fontFamily: 'inherit' } }}
          />
        ),
      },
      { field: "plant", headerName: t("enterQty.center"), width: 120 },
      {
        field: "message",
        headerName: t("log.message"),
        width: 250,
        renderCell: (params) => {
          const hasError = Boolean(params.value);
          return (
            <span
              style={{
                color: hasError ? "#ef4444" : "#22c55e",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              {params.value || "OK"}
            </span>
          );
        },
      },
    ].filter((col) => !(isTransfer && (col.field === "warehouse" || col.field === "batch"))),
    [t, updateRowField, selectedId, isSaved, depositos, isTransfer],
  );

  if (!selected) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography>{t("message.msg0001")}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* TÍTULO DA PÁGINA */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <IconButton
          sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
          size="small"
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em" }}
        >
          {t("enterQty.title")} -{" "}
          <Typography
            component="span"
            variant="h5"
            sx={{ color: "primary.main", fontFamily: "monospace", fontWeight: 700 }}
          >
            {accessKey}
          </Typography>
        </Typography>
      </Box>

      {/* CABEÇALHO DO DOCUMENTO */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "16px",
          overflow: "hidden",
          p: 2.5,
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        {headerInfo && <AssignmentHeaderInfo headerInfo={headerInfo} />}
      </Paper>

      {/* TOOLBAR + TABELA */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          mb: 2,
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 1 }}>
          <Tooltip title={t("buttons.duplicateItem")}>
            <span>
              <IconButton
                size="small"
                onClick={handleAddDocument}
                disabled={isSaved || !selectedId}
                sx={{
                  color: "#3b82f6",
                  bgcolor: chipBg("#3b82f6", "#eff6ff"),
                  borderRadius: "8px",
                  "&:hover": { bgcolor: isDark ? alpha("#3b82f6", 0.28) : "#dbeafe" },
                }}
              >
                <PostAddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("buttons.deleteItem")}>
            <span>
              <IconButton
                size="small"
                onClick={handleDeleteRow}
                disabled={isSaved || !selectedId}
                sx={{
                  color: "#ef4444",
                  bgcolor: chipBg("#ef4444", "#fef2f2"),
                  borderRadius: "8px",
                  "&:hover": { bgcolor: isDark ? alpha("#ef4444", 0.28) : "#fecaca" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title={t("buttons.bSave")}>
            <span>
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={isSaved}
                sx={{
                  color: "#10b981",
                  bgcolor: chipBg("#10b981", "#f0fdf4"),
                  borderRadius: "8px",
                  "&:hover": { bgcolor: isDark ? alpha("#10b981", 0.28) : "#dcfce7" },
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={rowsWithMessage}
            columns={columns}
            disableRowSelectionOnClick
            density="compact"
            onRowClick={(params) => {
              if (isSaved) return;
              setSelectedId(params.id);
            }}
            hideFooter
            getRowClassName={(params) => {
              const valid = isTransfer || isDepositValid(params.row.warehouse);
              return valid ? "row-status-success" : "row-status-error";
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "background.subtle",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiDataGrid-cell": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: "0.75rem",
                color: "text.secondary",
                textTransform: "uppercase",
              },
              "& .row-status-success .MuiDataGrid-cell:first-of-type": {
                borderLeft: "4px solid #22c55e",
              },
              "& .row-status-error .MuiDataGrid-cell:first-of-type": {
                borderLeft: "4px solid #ef4444",
              },
              "& .editable-cell": {
                backgroundColor: "action.hover",
                cursor: "pointer",
              },
            }}
          />
        </Box>
      </Paper>

      {/* MENSAGENS DO PROCESSO (mesmo painel, sempre visível, da tela de Atribuição de Pedido) */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
          maxHeight: 180,
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        <MessagesPanel messages={processMessages} />
      </Paper>

      {/* BOTÕES */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ border: "1px solid", borderColor: "divider", color: "text.primary", px: 4, textTransform: "none" }}
          >
            {t("buttons.bClose")}
          </Button>

          <Button
            variant="contained"
            onClick={handleProcess}
            disabled={isSaved}
            sx={{ px: 4, textTransform: "none" }}
          >
            {t("buttons.bProcess")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EnterQtyPage;